import { compareOrchestrationActivitiesByOrder } from "@afar-research/client-runtime/state/orchestration-activity-order";
import {
  APP_CONTROL_MCP_SERVER_NAME,
  WORKFLOW_DEFINITION_FILE,
  WORKFLOWS_DIRECTORY,
  type OrchestrationThreadActivity,
} from "@afar-research/contracts";

/**
 * Where a thread shows a Workflow card: after an agent's App Control call that
 * presents, runs, or reads a Workflow, and after a turn that edited a Workflow
 * definition. Both are read from turn data; nothing is added to the
 * conversation (ADR 0141).
 */

const WORKFLOW_CARD_TOOLS = ["workflow_present", "workflow_run", "workflow_get_run"] as const;
export type WorkflowCardTool = (typeof WORKFLOW_CARD_TOOLS)[number];

export interface WorkflowToolCard {
  readonly id: string;
  readonly turnId: string | null;
  readonly createdAt: string;
  readonly sequence?: number;
  readonly tool: WorkflowCardTool;
  readonly workflowName: string;
  readonly nodeId: string | null;
  readonly runId: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function parseJsonRecord(text: string): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(text));
  } catch {
    return null;
  }
}

/** An MCP tool result as a record: structured content, or the JSON its text block holds. */
function toolResultRecord(result: unknown): Record<string, unknown> | null {
  if (typeof result === "string") return parseJsonRecord(result);
  const content = Array.isArray(result) ? result : asRecord(result)?.content;
  const structured = asRecord(asRecord(result)?.structuredContent);
  if (structured) return structured;
  if (Array.isArray(content)) {
    for (const block of content) {
      const text = asString(asRecord(block)?.text);
      const parsed = text ? parseJsonRecord(text) : null;
      if (parsed) return parsed;
    }
  }
  return null;
}

function isWorkflowCardTool(tool: string): tool is WorkflowCardTool {
  return (WORKFLOW_CARD_TOOLS as ReadonlyArray<string>).includes(tool);
}

const CLAUDE_MCP_PREFIX = `mcp__${APP_CONTROL_MCP_SERVER_NAME}__`;

/** Claude names a tool only on its start activity: `mcp__<server>__<tool> started`. */
function claudeToolName(activity: OrchestrationThreadActivity): string | null {
  const name = activity.summary.replace(/ started$/, "");
  return name.startsWith("mcp__") ? name : null;
}

interface PendingCall {
  readonly activity: OrchestrationThreadActivity;
  readonly tool: string | null;
  input: Record<string, unknown>;
  result: Record<string, unknown> | null;
}

function toCard(call: PendingCall): WorkflowToolCard | null {
  if (call.tool === null || !isWorkflowCardTool(call.tool)) return null;
  const workflowName = asString(call.input.workflow) ?? asString(call.result?.workflow);
  if (workflowName === null) return null;
  return {
    id: `workflow-card:${call.activity.id}`,
    turnId: call.activity.turnId,
    createdAt: call.activity.createdAt,
    ...(call.activity.sequence !== undefined ? { sequence: call.activity.sequence } : {}),
    tool: call.tool,
    workflowName,
    nodeId: asString(call.input.nodeId),
    runId: asString(call.result?.runId) ?? asString(call.input.runId),
  };
}

const TOOL_ACTIVITY_KINDS = new Set(["tool.started", "tool.updated", "tool.completed"]);

export function deriveWorkflowToolCards(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): WorkflowToolCard[] {
  const codexCalls = new Map<string, PendingCall>();
  const claudeCalls: PendingCall[] = [];
  // Claude reports a call's input and result without its name; within a turn, input
  // belongs to the call started last and results arrive in the order calls started.
  const openByTurn = new Map<string, PendingCall[]>();
  for (const activity of [...activities].toSorted(compareOrchestrationActivitiesByOrder)) {
    if (!TOOL_ACTIVITY_KINDS.has(activity.kind)) continue;
    const payload = asRecord(activity.payload);
    if (payload?.itemType !== "mcp_tool_call") continue;
    const data = asRecord(payload.data);
    const item = asRecord(data?.item);
    if (item?.type === "mcpToolCall") {
      const id = asString(item.id) ?? activity.id;
      const tool =
        item.server === APP_CONTROL_MCP_SERVER_NAME ? (asString(item.tool) ?? null) : null;
      const existing = codexCalls.get(id);
      const call = existing ?? { activity, tool, input: {}, result: null };
      call.input = asRecord(item.arguments) ?? call.input;
      call.result = toolResultRecord(item.result) ?? call.result;
      codexCalls.set(id, call);
      continue;
    }
    const turnKey = activity.turnId ?? "";
    const open = openByTurn.get(turnKey) ?? [];
    openByTurn.set(turnKey, open);
    if (activity.kind === "tool.started") {
      const name = claudeToolName(activity);
      const call: PendingCall = {
        activity,
        tool: name?.startsWith(CLAUDE_MCP_PREFIX) ? name.slice(CLAUDE_MCP_PREFIX.length) : null,
        input: asRecord(data?.input) ?? {},
        result: null,
      };
      open.push(call);
      claudeCalls.push(call);
      continue;
    }
    if (activity.kind === "tool.updated") {
      const input = asRecord(data?.input);
      const latest = open.at(-1);
      if (latest && input && Object.keys(input).length > 0) latest.input = input;
      continue;
    }
    const oldest = open.shift();
    if (oldest) oldest.result = toolResultRecord(data?.result);
  }
  return [...claudeCalls, ...codexCalls.values()].flatMap((call) => {
    const card = toCard(call);
    return card ? [card] : [];
  });
}

const DEFINITION_PATH = new RegExp(
  `(?:^|/)${WORKFLOWS_DIRECTORY.replaceAll(".", "\\.")}/([^/]+)/${WORKFLOW_DEFINITION_FILE.replaceAll(".", "\\.")}$`,
);

/** The Workflows whose definition file a turn changed, once each, in file order. */
export function workflowNamesInChangedFiles(paths: ReadonlyArray<string>): string[] {
  return [
    ...new Set(
      paths.flatMap((path) => {
        const match = DEFINITION_PATH.exec(path.replaceAll("\\", "/"));
        return match?.[1] ? [match[1]] : [];
      }),
    ),
  ];
}
